// src/pages/LandingPage.jsx
// AgentBlayna — Invite-Only Luxury Landing (LIGHT, Apple-level calm)
// ✅ Landing is ALWAYS public (NO auto redirect)
// ✅ Login button uses SPA routing
// ✅ Invite-only tone, premium spacing, no loud black/white billboard text
//
// Logo: src/assets/Agent-Blayna-Logo.png

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";

import logo from "../assets/Agent-Blayna-Logo.png";

/* --------------------------------- helpers -------------------------------- */

function useRevealOnScroll(threshold = 0.18) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setVisible(true);
      },
      { threshold }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return { ref, visible };
}

const premium = {
  ink: "#0B0F1A",
  bg: "#F6F7F9",
  subtle: alpha("#0B0F1A", 0.62),
  border: alpha("#0B0F1A", 0.10),
  shadow: alpha("#0B0F1A", 0.10),
  blue: "#2D5BFF",
};

const styles = {
  page: {
    minHeight: "100vh",
    bgcolor: premium.bg,
    color: premium.ink,
    overflow: "hidden",
    position: "relative",
  },
  navLink: {
    textTransform: "none",
    fontWeight: 900,
    fontSize: 13.5,
    letterSpacing: -0.1,
    color: alpha(premium.ink, 0.70),
    "&:hover": { bgcolor: "transparent", color: premium.ink },
  },
  primaryBtn: {
    textTransform: "none",
    fontWeight: 950,
    borderRadius: 999,
    px: 3,
    py: 1.25,
    bgcolor: premium.ink,
    color: "#fff",
    boxShadow: `0 18px 50px ${alpha(premium.ink, 0.14)}`,
    "&:hover": { bgcolor: premium.ink },
    "&:active": { transform: "translateY(1px)" },
  },
  secondaryBtn: {
    textTransform: "none",
    fontWeight: 950,
    borderRadius: 999,
    px: 3,
    py: 1.25,
    borderColor: alpha(premium.ink, 0.16),
    color: premium.ink,
    bgcolor: alpha("#fff", 0.85),
    "&:hover": {
      borderColor: alpha(premium.ink, 0.24),
      bgcolor: "#fff",
    },
  },
};

/* -------------------------------- components ------------------------------- */

function SoftCard({ children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 7,
        bgcolor: alpha("#fff", 0.82),
        border: `1px solid ${premium.border}`,
        backdropFilter: "blur(18px)",
        boxShadow: `0 30px 110px ${alpha(premium.ink, 0.09)}`,
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function Hairline({ sx }) {
  return (
    <Box
      sx={{
        height: 1,
        width: "100%",
        bgcolor: alpha(premium.ink, 0.08),
        ...sx,
      }}
    />
  );
}

function MiniTrustRow() {
  const items = [
    { icon: <ShieldRoundedIcon sx={{ fontSize: 17 }} />, text: "Limited seats per city" },
    { icon: <VerifiedRoundedIcon sx={{ fontSize: 17 }} />, text: "Built for serious agents" },
    { icon: <AutoAwesomeRoundedIcon sx={{ fontSize: 17 }} />, text: "Quiet automation (not spam)" },
  ];

  return (
    <Stack direction="row" spacing={1.1} sx={{ flexWrap: "wrap" }}>
      {items.map((it) => (
        <Stack
          key={it.text}
          direction="row"
          spacing={0.9}
          alignItems="center"
          sx={{
            px: 1.2,
            py: 0.7,
            borderRadius: 999,
            bgcolor: alpha("#fff", 0.82),
            border: `1px solid ${alpha(premium.ink, 0.10)}`,
          }}
        >
          <Box sx={{ color: alpha(premium.ink, 0.70), display: "grid", placeItems: "center" }}>
            {it.icon}
          </Box>
          <Typography sx={{ fontSize: 12.8, fontWeight: 900, color: alpha(premium.ink, 0.70) }}>
            {it.text}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function SectionTitle({ eyebrow, title, desc, align = "left" }) {
  return (
    <Stack spacing={1.2} sx={{ textAlign: align }}>
      {!!eyebrow && (
        <Typography
          sx={{
            fontSize: 12,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            fontWeight: 950,
            color: alpha(premium.ink, 0.55),
          }}
        >
          {eyebrow}
        </Typography>
      )}

      <Typography
        sx={{
          fontWeight: 990,
          letterSpacing: -1.2,
          lineHeight: 1.08,
          fontSize: { xs: 28, md: 42 },
        }}
      >
        {title}
      </Typography>

      {!!desc && (
        <Typography
          sx={{
            color: alpha(premium.ink, 0.68),
            fontSize: { xs: 15.5, md: 17 },
            lineHeight: 1.8,
            maxWidth: align === "center" ? 820 : 880,
            mx: align === "center" ? "auto" : 0,
          }}
        >
          {desc}
        </Typography>
      )}
    </Stack>
  );
}

function LuxePreview() {
  return (
    <Box
      sx={{
        position: "relative",
        perspective: "1200px",
      }}
    >
      {/* Multi-layer glow effect */}
      <Box
        sx={{
          position: "absolute",
          top: "-10%",
          left: "-10%",
          right: "-10%",
          bottom: "-10%",
          background: `
            radial-gradient(ellipse at 30% 20%, ${alpha(premium.blue, 0.3)}, transparent 50%),
            radial-gradient(ellipse at 70% 80%, ${alpha("#7A5CFF", 0.25)}, transparent 50%)
          `,
          filter: "blur(60px)",
          zIndex: 0,
        }}
      />

      {/* Main app window - Liquid Glass */}
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 1,
          borderRadius: 2.5,
          overflow: "hidden",
          // Liquid Glass effect
          bgcolor: alpha("#fff", 0.65),
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          border: `1px solid ${alpha("#fff", 0.4)}`,
          boxShadow: `
            0 4px 6px ${alpha(premium.ink, 0.02)},
            0 12px 24px ${alpha(premium.ink, 0.06)},
            0 32px 64px ${alpha(premium.ink, 0.1)},
            inset 0 1px 0 ${alpha("#fff", 0.6)}
          `,
          transform: "rotateY(-2deg) rotateX(2deg)",
          transformStyle: "preserve-3d",
          transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "rotateY(0deg) rotateX(0deg)",
          },
        }}
      >
        {/* Window title bar */}
        <Box
          sx={{
            px: 2,
            py: 1.2,
            borderBottom: `1px solid ${alpha(premium.ink, 0.08)}`,
            bgcolor: alpha(premium.ink, 0.02),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            {/* Traffic lights */}
            <Stack direction="row" spacing={0.7}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#FF5F57" }} />
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#FFBD2E" }} />
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#28CA41" }} />
            </Stack>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: alpha(premium.ink, 0.6), ml: 1 }}>
              AgentBlayna
            </Typography>
          </Stack>

          <Chip
            size="small"
            icon={<LockRoundedIcon sx={{ fontSize: 14 }} />}
            label="Private"
            sx={{
              height: 24,
              bgcolor: alpha(premium.ink, 0.06),
              border: "none",
              fontWeight: 700,
              fontSize: 11,
              color: alpha(premium.ink, 0.65),
              "& .MuiChip-icon": { color: alpha(premium.ink, 0.5) },
            }}
          />
        </Box>

        {/* App content area */}
        <Box
          sx={{
            p: 2.5,
            background: `linear-gradient(135deg, ${alpha(premium.blue, 0.03)} 0%, ${alpha("#7A5CFF", 0.02)} 100%)`,
            minHeight: 340,
          }}
        >
          {/* Two-column layout */}
          <Grid container spacing={2}>
            {/* Left column - Today's priorities */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: `1px solid ${alpha(premium.ink, 0.08)}`,
                  boxShadow: `0 2px 8px ${alpha(premium.ink, 0.06)}`,
                  height: "100%",
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: premium.ink, mb: 0.5 }}>
                  Today — what matters
                </Typography>
                <Typography sx={{ fontSize: 12, color: alpha(premium.ink, 0.55), mb: 2 }}>
                  A calm list of priority actions.
                </Typography>

                <Stack spacing={1.2}>
                  {[
                    { t: "Follow up: Carter", s: "due today", tag: "warm lead", tagColor: "#22C55E" },
                    { t: "Anniversary: S + J", s: "3 days", tag: "message ready", tagColor: premium.blue },
                    { t: "Closing: Maple St", s: "10 days", tag: "checklist", tagColor: "#F59E0B" },
                  ].map((x, i) => (
                    <Box
                      key={x.t}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: i === 0 ? alpha(premium.blue, 0.06) : alpha(premium.ink, 0.02),
                        border: `1px solid ${i === 0 ? alpha(premium.blue, 0.15) : alpha(premium.ink, 0.06)}`,
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: premium.ink }}>
                            {x.t}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: alpha(premium.ink, 0.5), mt: 0.3 }}>
                            {x.s}
                          </Typography>
                        </Box>
                        <Chip
                          label={x.tag}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: alpha(x.tagColor, 0.12),
                            color: x.tagColor,
                            border: "none",
                            ml: 1,
                            flexShrink: 0,
                          }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* Right column - Relationship Engine */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: `1px solid ${alpha(premium.ink, 0.08)}`,
                  boxShadow: `0 2px 8px ${alpha(premium.ink, 0.06)}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: premium.ink }}>
                    Relationship Engine
                  </Typography>
                  <Stack direction="row" spacing={0.8}>
                    <Chip
                      size="small"
                      icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 14 }} />}
                      label="Alerts"
                      sx={{
                        height: 26,
                        bgcolor: alpha(premium.ink, 0.05),
                        border: "none",
                        fontWeight: 700,
                        fontSize: 11,
                        "& .MuiChip-icon": { fontSize: 14 },
                      }}
                    />
                    <Chip
                      size="small"
                      icon={<InsightsRoundedIcon sx={{ fontSize: 14 }} />}
                      label="Trends"
                      sx={{
                        height: 26,
                        bgcolor: alpha(premium.blue, 0.08),
                        color: premium.blue,
                        border: "none",
                        fontWeight: 700,
                        fontSize: 11,
                        "& .MuiChip-icon": { fontSize: 14, color: premium.blue },
                      }}
                    />
                  </Stack>
                </Stack>

                <Typography sx={{ fontSize: 12.5, color: alpha(premium.ink, 0.6), lineHeight: 1.7, mb: 2 }}>
                  You shouldn't "try to remember." You should have a system that quietly makes it impossible to forget.
                </Typography>

                {/* AI Suggestion card */}
                <Box
                  sx={{
                    mt: "auto",
                    p: 2,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${alpha(premium.blue, 0.08)} 0%, ${alpha("#7A5CFF", 0.06)} 100%)`,
                    border: `1px solid ${alpha(premium.blue, 0.15)}`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: premium.blue }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 12, color: premium.blue }}>
                      AI Suggestion Ready
                    </Typography>
                  </Stack>

                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: premium.ink, lineHeight: 1.5, mb: 1.5 }}>
                    "Quick note — I wanted to make sure you're taken care of this week…"
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Chip
                      size="small"
                      icon={<CalendarMonthRoundedIcon sx={{ fontSize: 14 }} />}
                      label="timed perfectly"
                      sx={{
                        height: 24,
                        bgcolor: alpha("#fff", 0.8),
                        border: `1px solid ${alpha(premium.ink, 0.1)}`,
                        fontWeight: 700,
                        fontSize: 11,
                        "& .MuiChip-icon": { fontSize: 14 },
                      }}
                    />
                    <Chip
                      size="small"
                      icon={<CheckCircleRoundedIcon sx={{ fontSize: 14, color: "#22C55E" }} />}
                      label="human tone"
                      sx={{
                        height: 24,
                        bgcolor: alpha("#fff", 0.8),
                        border: `1px solid ${alpha(premium.ink, 0.1)}`,
                        fontWeight: 700,
                        fontSize: 11,
                        "& .MuiChip-icon": { fontSize: 14 },
                      }}
                    />
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

/* ---------------------------------- page ---------------------------------- */

export default function LandingPage() {
  const navigate = useNavigate();
  const isMdUp = useMediaQuery("(min-width:900px)");
  const [showTop, setShowTop] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > 900);
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onLogin = () => {
    // ✅ Always require email/password at /login (no auto auth routing here)
    navigate("/login");
  };

  const onSignup = () => navigate("/signup");

  const bg = `
    radial-gradient(1100px circle at 16% 0%, rgba(45, 91, 255, 0.15), transparent 55%),
    radial-gradient(1100px circle at 88% 12%, rgba(122, 92, 255, 0.10), transparent 60%),
    radial-gradient(1200px circle at 50% 110%, rgba(0, 0, 0, 0.06), transparent 60%),
    linear-gradient(180deg, #F7F8FA 0%, #F3F4F7 40%, #F7F8FA 100%)
  `;

  return (
    <Box sx={styles.page}>
      {/* background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: bg,
        }}
      />

      {/* subtle grain */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.05,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(0,0,0,.35) 1px, transparent 0)",
          backgroundSize: "20px 20px",
        }}
      />

      {/* STICKY NAVBAR - Liquid Glass */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          bgcolor: scrolled ? alpha("#fff", 0.72) : "transparent",
          backdropFilter: scrolled ? "blur(20px) saturate(180%)" : "none",
          borderBottom: scrolled ? `1px solid ${alpha(premium.ink, 0.08)}` : "1px solid transparent",
          boxShadow: scrolled ? `0 4px 30px ${alpha(premium.ink, 0.08)}` : "none",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: scrolled ? 1.5 : 2.5, px: { xs: 1, md: 2 }, transition: "padding 0.3s ease" }}
          >
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Box component="img" src={logo} alt="AgentBlayna" sx={{ width: 36, height: 36, borderRadius: 2 }} />
              <Typography sx={{ fontWeight: 990, letterSpacing: -0.3, fontSize: 14.8 }}>
                AgentBlayna
              </Typography>

              {isMdUp && (
                <Chip
                  icon={<LockRoundedIcon sx={{ fontSize: 14 }} />}
                  label="Limited seats"
                  size="small"
                  sx={{
                    ml: 0.8,
                    height: 26,
                    bgcolor: scrolled ? alpha(premium.ink, 0.06) : alpha("#fff", 0.7),
                    border: `1px solid ${alpha(premium.ink, 0.1)}`,
                    fontWeight: 800,
                    fontSize: 12,
                    backdropFilter: "blur(8px)",
                    transition: "all 0.3s ease",
                  }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {isMdUp && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 1 }}>
                  <Button onClick={scrollTo("why")} variant="text" sx={styles.navLink}>
                    Why
                  </Button>
                  <Button onClick={scrollTo("features")} variant="text" sx={styles.navLink}>
                    Features
                  </Button>
                  <Button onClick={scrollTo("faq")} variant="text" sx={styles.navLink}>
                    FAQ
                  </Button>
                </Stack>
              )}

              <Button onClick={onSignup} variant="text" sx={{ ...styles.navLink, display: { xs: "none", sm: "inline-flex" } }}>
                Sign up
              </Button>

              <Button onClick={onLogin} variant="contained" disableElevation sx={{ ...styles.primaryBtn, px: 2.2, py: 0.9 }}>
                Log in
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Main content with top padding for fixed nav */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, pt: 14, pb: 10 }}>
        {/* HERO */}
        <Grid container spacing={4.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={2.4} sx={{ pr: { md: 3 } }}>
              <MiniTrustRow />

              <Typography
                sx={{
                  fontWeight: 995,
                  letterSpacing: -2.2,
                  lineHeight: 1.03,
                  fontSize: { xs: 40, md: 64 },
                  maxWidth: 560,
                }}
              >
                Relationships shouldn’t depend on memory.
              </Typography>

              <Typography
                sx={{
                  color: alpha(premium.ink, 0.68),
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: 1.9,
                  maxWidth: 560,
                }}
              >
                AgentBlayna is a private operating system built for agents who protect their reputation.
                It keeps follow-ups, key dates, and deal moments handled — automatically — without feeling robotic.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.3} sx={{ pt: 0.3 }}>
                <Button
                  onClick={onSignup}
                  size="large"
                  variant="contained"
                  disableElevation
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={styles.primaryBtn}
                >
                  Start free trial
                </Button>

                <Button onClick={onLogin} size="large" variant="outlined" sx={styles.secondaryBtn}>
                  Log in
                </Button>
              </Stack>

              <Typography sx={{ fontSize: 12.8, color: alpha(premium.ink, 0.52), lineHeight: 1.7 }}>
                Limited seats per city. You’ll never be “one of thousands.”
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <LuxePreview />
          </Grid>
        </Grid>

        <Divider sx={{ my: 8, borderColor: alpha(premium.ink, 0.08) }} />

        {/* WHY */}
        <Box id="why" sx={{ scrollMarginTop: 90 }}>
          <Grid container spacing={3.5} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  bgcolor: "#fff",
                  border: `1px solid ${alpha(premium.ink, 0.08)}`,
                  height: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    fontWeight: 950,
                    color: alpha(premium.ink, 0.55),
                    mb: 1,
                  }}
                >
                  The why
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 990,
                    letterSpacing: -1.2,
                    lineHeight: 1.08,
                    fontSize: { xs: 28, md: 38 },
                    mb: 1.5,
                  }}
                >
                  Real estate is still running on memory.
                </Typography>
                <Typography
                  sx={{
                    color: alpha(premium.ink, 0.68),
                    fontSize: 15,
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  In a job designed to interrupt you all day, memory is the wrong operating system. AgentBlayna quietly carries the mental load.
                </Typography>

                <Stack spacing={2}>
                  {[
                    {
                      t: "Consistency is what closes.",
                      d: "People trust who shows up on time — every time.",
                    },
                    {
                      t: "Your pipeline should feel calm.",
                      d: "When priorities surface automatically, your day becomes lighter and more precise.",
                    },
                    {
                      t: "Luxury is reliability.",
                      d: "Not louder marketing. Not more hustle. Just a system that prevents drop-offs.",
                    },
                  ].map((x) => (
                    <Stack key={x.t} spacing={0.5}>
                      <Typography sx={{ fontWeight: 800, letterSpacing: -0.3, fontSize: 15 }}>
                        {x.t}
                      </Typography>
                      <Typography sx={{ color: alpha(premium.ink, 0.6), lineHeight: 1.7, fontSize: 14 }}>
                        {x.d}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  bgcolor: "#fff",
                  border: `1px solid ${alpha(premium.ink, 0.08)}`,
                  height: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    fontWeight: 950,
                    color: alpha(premium.ink, 0.55),
                    mb: 1,
                  }}
                >
                  What it is
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 990,
                    letterSpacing: -1.2,
                    lineHeight: 1.08,
                    fontSize: { xs: 28, md: 38 },
                    mb: 1.5,
                  }}
                >
                  A private client operating system.
                </Typography>
                <Typography
                  sx={{
                    color: alpha(premium.ink, 0.68),
                    fontSize: 15,
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  AgentBlayna sits beside whatever tools you already use. It doesn't replace you — it protects you.
                </Typography>

                {/* 2x2 Feature Grid */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <Box sx={{ p: 2, borderRadius: 3, bgcolor: alpha("#EF4444", 0.04), border: `1px solid ${alpha("#EF4444", 0.12)}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                      <NotificationsActiveRoundedIcon sx={{ fontSize: 20, color: "#EF4444" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Priority surfacing</Typography>
                    </Stack>
                    <Typography sx={{ color: alpha(premium.ink, 0.6), fontSize: 13, lineHeight: 1.6 }}>
                      The right client. The right moment.
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, borderRadius: 3, bgcolor: alpha("#8B5CF6", 0.04), border: `1px solid ${alpha("#8B5CF6", 0.12)}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                      <CalendarMonthRoundedIcon sx={{ fontSize: 20, color: "#8B5CF6" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Lifecycle reminders</Typography>
                    </Stack>
                    <Typography sx={{ color: alpha(premium.ink, 0.6), fontSize: 13, lineHeight: 1.6 }}>
                      Birthdays, anniversaries, milestones.
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, borderRadius: 3, bgcolor: alpha("#3B82F6", 0.04), border: `1px solid ${alpha("#3B82F6", 0.12)}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                      <InsightsRoundedIcon sx={{ fontSize: 20, color: "#3B82F6" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Pipeline clarity</Typography>
                    </Stack>
                    <Typography sx={{ color: alpha(premium.ink, 0.6), fontSize: 13, lineHeight: 1.6 }}>
                      See what's moving and what matters.
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, borderRadius: 3, bgcolor: alpha("#10B981", 0.04), border: `1px solid ${alpha("#10B981", 0.12)}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                      <AutoAwesomeRoundedIcon sx={{ fontSize: 20, color: "#10B981" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Quiet assistance</Typography>
                    </Stack>
                    <Typography sx={{ color: alpha(premium.ink, 0.6), fontSize: 13, lineHeight: 1.6 }}>
                      Suggestions that still feel human.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* FEATURES */}
        <Box id="features" sx={{ mt: 9, scrollMarginTop: 90 }}>
          <SectionTitle
            eyebrow="Designed for execution"
            title="Invite. Setup. Calm execution."
            desc="Once you're inside, AgentBlayna becomes the part of your business that never forgets."
          />

          <Grid container spacing={2.5} sx={{ mt: 3 }}>
            {[
              {
                icon: <PersonAddRoundedIcon />,
                title: "Seat-limited onboarding",
                desc: "We keep it personal. No chaos. No support delays.",
                color: "#6366F1",
              },
              {
                icon: <RocketLaunchRoundedIcon />,
                title: "Real clients, instantly useful",
                desc: "Add a handful of relationships and it starts surfacing priorities immediately.",
                color: "#22C55E",
              },
              {
                icon: <HandshakeRoundedIcon />,
                title: "Trust compounds automatically",
                desc: "Follow-ups hit on time. Your name stays remembered.",
                color: "#F59E0B",
              },
            ].map((s) => (
              <Grid item xs={12} md={4} key={s.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    bgcolor: "#fff",
                    border: `1px solid ${alpha(premium.ink, 0.08)}`,
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 20px 40px ${alpha(premium.ink, 0.1)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(s.color, 0.1),
                      color: s.color,
                      mb: 2,
                    }}
                  >
                    {React.cloneElement(s.icon, { sx: { fontSize: 24 } })}
                  </Box>
                  <Typography sx={{ fontWeight: 800, letterSpacing: -0.4, fontSize: 18, mb: 1 }}>
                    {s.title}
                  </Typography>
                  <Typography sx={{ color: alpha(premium.ink, 0.65), lineHeight: 1.75, fontSize: 15 }}>
                    {s.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* OUTCOME */}
        <Box sx={{ mt: 9 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 5,
              bgcolor: "#fff",
              border: `1px solid ${alpha(premium.ink, 0.08)}`,
              boxShadow: `0 4px 20px ${alpha(premium.ink, 0.04)}`,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    fontWeight: 950,
                    color: "#22C55E",
                    mb: 1.5,
                  }}
                >
                  The result
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 990,
                    letterSpacing: -1.2,
                    lineHeight: 1.08,
                    fontSize: { xs: 28, md: 40 },
                    mb: 1.5,
                  }}
                >
                  Clients feel remembered — because they are.
                </Typography>
                <Typography sx={{ color: alpha(premium.ink, 0.65), fontSize: 16, lineHeight: 1.8, mb: 3 }}>
                  Your follow-up becomes automatic. Your brand becomes inevitable.
                </Typography>

                <Stack spacing={1.5}>
                  {[
                    "Warm leads don't cool off.",
                    "Past clients don't disappear.",
                    "Your pipeline stays readable.",
                    "Your day feels lighter — and you close cleaner.",
                  ].map((t) => (
                    <Stack direction="row" spacing={1.2} alignItems="center" key={t}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: alpha("#22C55E", 0.1),
                        }}
                      >
                        <CheckCircleRoundedIcon sx={{ fontSize: 16, color: "#22C55E" }} />
                      </Box>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: alpha(premium.ink, 0.75) }}>
                        {t}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    borderRadius: 4,
                    p: 3.5,
                    background: `linear-gradient(135deg, ${alpha(premium.blue, 0.04)} 0%, ${alpha("#7A5CFF", 0.04)} 100%)`,
                    border: `1px solid ${alpha(premium.blue, 0.12)}`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <LockRoundedIcon sx={{ fontSize: 18, color: premium.blue }} />
                    <Typography sx={{ fontWeight: 800, letterSpacing: -0.3, fontSize: 18 }}>
                      Invite-only for a reason.
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: alpha(premium.ink, 0.65), lineHeight: 1.8, mb: 3 }}>
                    The experience matters. We protect the calm.
                  </Typography>

                  <Stack spacing={1.2}>
                    <Button
                      onClick={onSignup}
                      variant="contained"
                      disableElevation
                      fullWidth
                      sx={{
                        ...styles.primaryBtn,
                        py: 1.5,
                      }}
                    >
                      Start free trial
                    </Button>
                    <Button
                      onClick={onLogin}
                      variant="outlined"
                      fullWidth
                      sx={{
                        ...styles.secondaryBtn,
                        py: 1.5,
                      }}
                    >
                      Log in
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* FAQ */}
        <Box id="faq" sx={{ mt: 10, scrollMarginTop: 90 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(premium.blue, 0.1),
                color: premium.blue,
              }}
            >
              <QuestionAnswerRoundedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                fontWeight: 950,
                color: alpha(premium.ink, 0.55),
              }}
            >
              FAQ
            </Typography>
          </Stack>

          <Typography
            sx={{
              fontWeight: 990,
              letterSpacing: -1.2,
              lineHeight: 1.08,
              fontSize: { xs: 28, md: 42 },
              mb: 1,
            }}
          >
            Simple answers. No fluff.
          </Typography>
          <Typography
            sx={{
              color: alpha(premium.ink, 0.68),
              fontSize: { xs: 15.5, md: 17 },
              lineHeight: 1.8,
              maxWidth: 600,
            }}
          >
            If it feels like your type of system, it probably is.
          </Typography>

          <Grid container spacing={2.5} sx={{ mt: 3 }}>
            {[
              {
                q: "Why is AgentBlayna invite-only?",
                a: "Because the product is meant to feel personal. Invite-only keeps onboarding clean, support responsive, and the system aligned with agents who care about brand and consistency.",
              },
              {
                q: "Do I have to replace my CRM?",
                a: "No. AgentBlayna sits beside whatever you already use. It protects follow-ups, key dates, and deal moments — without forcing you to change your workflow.",
              },
              {
                q: "What's the core promise?",
                a: "Relationships won't depend on who remembers the most. Your system carries the mental load so you stay present and consistent.",
              },
              {
                q: "What happens after I request access?",
                a: "We review requests manually. If approved, you'll receive private login details and a fast onboarding process using real client data.",
              },
            ].map((x, index) => (
              <Grid item xs={12} md={6} key={x.q}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    bgcolor: "#fff",
                    border: `1px solid ${alpha(premium.ink, 0.08)}`,
                    height: "100%",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: alpha(premium.blue, 0.2),
                      boxShadow: `0 8px 24px ${alpha(premium.ink, 0.06)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        minWidth: 28,
                        height: 28,
                        borderRadius: 1.5,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(premium.blue, 0.08),
                        color: premium.blue,
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 750, letterSpacing: -0.2, fontSize: 16, mb: 1 }}>
                        {x.q}
                      </Typography>
                      <Typography sx={{ color: alpha(premium.ink, 0.65), lineHeight: 1.8, fontSize: 14.5 }}>
                        {x.a}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FINAL CTA - Liquid Glass */}
        <Box sx={{ mt: 11, position: "relative" }}>
          {/* Background glow */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "120%",
              height: "120%",
              background: `
                radial-gradient(ellipse at 30% 30%, ${alpha(premium.blue, 0.15)}, transparent 50%),
                radial-gradient(ellipse at 70% 70%, ${alpha("#7A5CFF", 0.12)}, transparent 50%)
              `,
              filter: "blur(80px)",
              zIndex: 0,
            }}
          />
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              zIndex: 1,
              p: { xs: 4, md: 6 },
              borderRadius: 5,
              // Liquid Glass
              bgcolor: alpha("#fff", 0.6),
              backdropFilter: "blur(24px) saturate(180%)",
              WebkitBackdropFilter: "blur(24px) saturate(180%)",
              border: `1px solid ${alpha("#fff", 0.5)}`,
              boxShadow: `
                0 8px 32px ${alpha(premium.ink, 0.08)},
                inset 0 1px 0 ${alpha("#fff", 0.6)}
              `,
              overflow: "hidden",
            }}
          >
            {/* Decorative gradient orbs */}
            <Box
              sx={{
                position: "absolute",
                top: -100,
                right: -100,
                width: 300,
                height: 300,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(premium.blue, 0.08)} 0%, transparent 70%)`,
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -80,
                left: -80,
                width: 250,
                height: 250,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha("#7A5CFF", 0.06)} 0%, transparent 70%)`,
                pointerEvents: "none",
              }}
            />

            <Stack spacing={2.5} alignItems="center" textAlign="center" sx={{ position: "relative", zIndex: 1 }}>
              <Chip
                icon={<LockRoundedIcon sx={{ fontSize: 16 }} />}
                label="Limited seats per city"
                size="small"
                sx={{
                  bgcolor: alpha("#fff", 0.9),
                  border: `1px solid ${alpha(premium.ink, 0.12)}`,
                  fontWeight: 800,
                  boxShadow: `0 2px 8px ${alpha(premium.ink, 0.06)}`,
                }}
              />

              <Typography
                sx={{
                  fontWeight: 900,
                  letterSpacing: -1.5,
                  lineHeight: 1.1,
                  fontSize: { xs: 32, md: 52 },
                  maxWidth: 800,
                  background: `linear-gradient(135deg, ${premium.ink} 0%, ${alpha(premium.ink, 0.8)} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                You don't need more hustle.
                <br />
                You need a system that never forgets.
              </Typography>

              <Typography
                sx={{
                  color: alpha(premium.ink, 0.65),
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: 1.8,
                  maxWidth: 700,
                }}
              >
                AgentBlayna is built for agents who protect their reputation,
                respect their time, and refuse to let relationships slip.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.3} sx={{ pt: 0.5 }}>
                <Button onClick={onSignup} size="large" variant="contained" disableElevation sx={styles.primaryBtn}>
                  Start free trial
                </Button>
                <Button onClick={onLogin} size="large" variant="outlined" sx={styles.secondaryBtn}>
                  Log in
                </Button>
              </Stack>

              <Typography sx={{ fontSize: 13, color: alpha(premium.ink, 0.45), fontWeight: 500 }}>
                Built with intention. Designed to feel inevitable.
              </Typography>
            </Stack>
          </Paper>
        </Box>

        {/* footer */}
        <Box sx={{ mt: 8, pb: 4 }}>
          <Divider sx={{ mb: 2.2, borderColor: alpha(premium.ink, 0.08) }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
            <Typography sx={{ fontSize: 12.5, color: alpha(premium.ink, 0.55) }}>
              © {new Date().getFullYear()} AgentBlayna. Built with intention.
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: alpha(premium.ink, 0.45) }}>
              Limited seats per city • Private access
            </Typography>
          </Stack>
        </Box>
      </Container>

      {/* scroll-to-top */}
      {showTop ? (
        <Box sx={{ position: "fixed", right: 18, bottom: 18, zIndex: 1400 }}>
          <IconButton
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            sx={{
              width: 46,
              height: 46,
              borderRadius: 999,
              bgcolor: alpha("#fff", 0.92),
              border: `1px solid ${alpha(premium.ink, 0.12)}`,
              boxShadow: `0 18px 55px ${alpha(premium.ink, 0.12)}`,
              "&:hover": { bgcolor: "#fff" },
            }}
          >
            <KeyboardArrowUpRoundedIcon />
          </IconButton>
        </Box>
      ) : null}
    </Box>
  );
}
